// Downloads Admin Section - Admin interface for managing downloads
// Follows the Section, Component & Manager System architecture
// Uses same DownloadsLayout as root page with admin={true}

import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { DownloadsManager } from '../../managers/downloads-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { DownloadsLayout, DownloadsGrid, CreateEditDownloadModal, ManageTagsModal, DownloadDeleteConfirmModal } from '../../components';
import './DownloadsAdmin.css';

export function DownloadsAdminSection() {
  const { user, loading: authLoading } = useAuth();
  const { manager, isInitialized } = useManager(DownloadsManager);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [downloads, setDownloads] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editDownload, setEditDownload] = useState(null);
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [currentTags, setCurrentTags] = useState([]);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    download: null,
    isDeleting: false,
  });

  const urlQuery = searchParams.get('query') || '';
  const urlSort = searchParams.get('sort') || '';
  const urlTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];

  const fetchDownloads = useCallback(async () => {
    if (!manager) return;
    setDownloadsLoading(true);
    try {
      const list = await manager.fetchNewDownloads({
        query: urlQuery,
        sort: urlSort,
        tags: urlTags,
      });
      setDownloads(list);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
      setDownloads([]);
    } finally {
      setDownloadsLoading(false);
    }
  }, [manager, urlQuery, urlSort, urlTags.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isInitialized && manager && user?.role === 'admin') {
      fetchDownloads();
    }
  }, [isInitialized, manager, user?.role, fetchDownloads]);

  // Handle back to downloads
  const handleBackToDownloads = useCallback(() => {
    navigate('/downloads');
  }, [navigate]);

  const handleCreateDownloadClick = useCallback(() => {
    setEditDownload(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    await fetchDownloads();
  }, [fetchDownloads]);

  const handleManageTagsClick = useCallback(async () => {
    try {
      const tags = await manager.getTags();
      setCurrentTags(tags);
      setIsTagsModalOpen(true);
    } catch (err) {
      console.error('Failed to load tags:', err);
      // TODO: Show error notification
    }
  }, [manager]);

  const handleTagsSubmit = useCallback(async (tags) => {
    try {
      await manager.updateTags(tags);
      setIsTagsModalOpen(false);
      // TODO: Show success notification
    } catch (err) {
      console.error('Failed to update tags:', err);
      // TODO: Show error notification
    }
  }, [manager]);

  const handleTagsModalClose = useCallback(() => {
    setIsTagsModalOpen(false);
  }, []);

  const handleEdit = useCallback((download) => {
    setEditDownload(download);
    setIsCreateModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsCreateModalOpen(false);
    setEditDownload(null);
  }, []);

  const handlePinToggle = useCallback(async (download) => {
    if (!manager) return;
    try {
      await manager.toggleDownloadPin(download.id, !download.isPinned);
      await fetchDownloads(); // Refresh to show updated state
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      // TODO: Show error notification
    }
  }, [manager, fetchDownloads]);

  const handleDelete = useCallback((download) => {
    setDeleteModalState({
      isOpen: true,
      download,
      isDeleting: false,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModalState.download || !manager) return;

    setDeleteModalState(prev => ({ ...prev, isDeleting: true }));

    try {
      await manager.deleteDownload(deleteModalState.download.id);
      await fetchDownloads(); // Refresh the list
      setDeleteModalState({ isOpen: false, download: null, isDeleting: false });
    } catch (err) {
      console.error('Failed to delete download:', err);
      setDeleteModalState(prev => ({ ...prev, isDeleting: false }));
      // TODO: Show error notification
    }
  }, [manager, deleteModalState.download, fetchDownloads]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalState({ isOpen: false, download: null, isDeleting: false });
  }, []);

  const handleDownloadClick = useCallback((d) => {
    if (d?.id) navigate(`/downloads/admin/details?downloadId=${d.id}`);
  }, [navigate]);

  const handleReorder = useCallback(async (swaps) => {
    if (!manager || !swaps || swaps.length !== 2) return;

    try {
      const [swap1, swap2] = swaps;
      await manager.swapPinnedOrder(
        swap1.downloadId,
        swap1.pinnedOrder,
        swap2.downloadId,
        swap2.pinnedOrder
      );
      await fetchDownloads(); // Refresh to show updated order
    } catch (err) {
      console.error('Failed to reorder downloads:', err);
      // TODO: Show error notification
    }
  }, [manager, fetchDownloads]);

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--downloads">
        <div className="loading-container">
          <div>Loading downloads admin...</div>
        </div>
      </MainLayout>
    );
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <MainLayout contentClassName="main-content--downloads">
        <div className="downloads-section">
          <div className="admin-loading">
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Verifying admin access...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Check admin access - only show admin panel for admins
  if (!user || user.role !== 'admin') {
    return (
      <MainLayout contentClassName="main-content--downloads">
        <div className="downloads-section">
          <div className="access-denied">
            <i className="fa-solid fa-shield-alt" style={{ fontSize: '3rem', color: '#ff4444', marginBottom: '20px' }}></i>
            <h1>Access Denied</h1>
            <p>This admin section is only available to administrators.</p>
            <p>Please contact support if you believe this is an error.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--downloads">
      <div className="downloads-section">
        <DownloadsLayout
          admin={true}
          user={user}
          navigate={navigate}
          onBackToDownloads={handleBackToDownloads}
          onCreateDownloadClick={handleCreateDownloadClick}
          onManageTagsClick={handleManageTagsClick}
          urlQuery={urlQuery}
          urlSort={urlSort}
          urlTags={urlTags}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        >
          <DownloadsGrid
            downloads={downloads}
            loading={downloadsLoading}
            admin={true}
            emptyMessage="No downloads to manage yet."
            sortMode={urlSort}
            onDownloadClick={handleDownloadClick}
            onPinToggle={handlePinToggle}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReorder={handleReorder}
          />
        </DownloadsLayout>
        <CreateEditDownloadModal
          isOpen={isCreateModalOpen}
          onClose={handleEditModalClose}
          onSubmit={handleCreateSubmit}
          download={editDownload}
        />
        <ManageTagsModal
          isOpen={isTagsModalOpen}
          onClose={handleTagsModalClose}
          onSubmit={handleTagsSubmit}
          tags={currentTags}
        />
        <DownloadDeleteConfirmModal
          isOpen={deleteModalState.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          download={deleteModalState.download}
          isLoading={deleteModalState.isDeleting}
        />
      </div>
    </MainLayout>
  );
}